import React from "react";
import { useNavigate } from "react-router-dom";
import "./PiMenuButton.css";
import classNames from "classnames";
import { OnboardingTargetId } from "../onboarding/onboardingTargets";

interface PiMenuButtonProps {
  text: string;
  id?: string;
  /**
   * Onboarding anchor for the rendered button. Deliberately separate from
   * `id`: see the registry doc comment for why tour anchors are never reused
   * from ids or test ids.
   */
  onboardingId?: OnboardingTargetId;
  icon?: React.ReactNode;
  /**
   * Optional second line, `variant="modern"` only. When set the item becomes a
   * two-line stack and stops truncating; when absent the markup is unchanged,
   * so existing single-line menus render exactly as before.
   */
  description?: string;
  link?: string;
  doHighlight?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  label?: string;
  externalLink?: boolean;
  variant?: "default" | "modern";
  tone?: "default" | "danger";
  /** Trailing icon, `variant="modern"` only — e.g. the chevron on a submenu row. */
  endIcon?: React.ReactNode;
  /** Set on a row that opens a submenu; drives aria-haspopup / aria-expanded. */
  submenuOpen?: boolean;
}

export default function PiMenuButton(props: PiMenuButtonProps) {
  const navigate = useNavigate();

  const onClick = () => {
    if (props.disabled) return; // ignore if button is disabled

    if (props.link) {
      if (props.externalLink) {
        window.open(props.link, "_blank");
      } else {
        navigate(props.link);
      }
    }
    if (props.onClick) {
      props.onClick();
    }
  };

  const label = (
    <div className="pimenu-button-label">
      <div className="pimenu-button-label-content">{props.label}</div>
    </div>
  );

  const hasDescription =
    props.variant === "modern" && Boolean(props.description);

  return (
    <button
      id={props.id}
      data-onboarding-id={props.onboardingId}
      aria-haspopup={props.submenuOpen !== undefined ? "menu" : undefined}
      aria-expanded={props.submenuOpen}
      className={classNames({
        "w-full flex gap-3 px-3 py-2.5 rounded-md transition-colors cursor-pointer outline-none text-left font-poppins":
          props.variant === "modern",
        // A two-line item hangs its icon off the first line, not the block.
        "items-start": hasDescription,
        "items-center": props.variant === "modern" && !hasDescription,
        // Not `primary-background`: that token is the page behind the menu, and
        // in dark mode it is darker than the menu's own surface, so hovering a
        // row punched a hole in the panel instead of lifting it.
        "hover:bg-creator-surfaceHover": props.variant === "modern" && !props.disabled,
        "opacity-55 pointer-events-none": props.variant === "modern" && props.disabled,
        "flex overflow-x-hidden overflow-y-hidden align-middle text-left h-[50px]  font-[14px] cursor-pointer w-full rounded-md select-none":
          props.variant !== "modern",
        "bg-creator-primary-washed": props.variant !== "modern" && props.doHighlight,
        "hover:bg-interaction-text-hoverBackground": props.variant !== "modern",
        "bg-creator-primary-background  pointer-events-none text-creator-primary-disabled":
          props.variant !== "modern" && props.disabled,
      })}
      onClick={() => onClick()}
    >
      {props.icon && (
        <div
          className={classNames({
            "flex items-center justify-center text-[20px] flex-shrink-0":
              props.variant === "modern",
            "text-creator-primary": props.variant === "modern" && props.tone !== "danger",
            "text-creator-error": props.variant === "modern" && props.tone === "danger",
            "px-2 text-[20px] my-auto flex w-[42px]": props.variant !== "modern",
            "text-creator-primary ": props.variant !== "modern" && props.doHighlight,
            "mt-0.5": hasDescription,
          })}
        >
          {props.icon}
        </div>
      )}
      {hasDescription ? (
        <div className="min-w-0 flex-1 flex flex-col gap-0.5">
          <div
            className={classNames("text-[13px] font-semibold leading-tight", {
              "text-creator-text": props.tone !== "danger",
              "text-creator-error": props.tone === "danger",
            })}
          >
            {props.text}
          </div>
          {/* `text-sub` and `text` resolve to the same #e0e4e8 in dark, which
              flattens the two lines into one block — so the second line drops
              to `text-soft` there. It is the same hue in both themes, but only
              in dark is it the one that separates them. */}
          <div className="text-[11px] font-normal leading-snug text-creator-text-sub dark:text-creator-text-soft">
            {props.description}
          </div>
        </div>
      ) : (
        <div
          className={classNames({
            "overflow-hidden whitespace-nowrap text-ellipsis text-[14px] font-medium":
              props.variant === "modern",
            "text-creator-text-sub": props.variant === "modern" && props.tone !== "danger",
            "text-creator-error": props.variant === "modern" && props.tone === "danger",
            "my-auto overflow-hidden whitespace-nowrap text-ellipsis text-[14px] ml-1":
              props.variant !== "modern",
            "text-creator-primary": props.variant !== "modern" && props.doHighlight,
          })}
        >
          {props.text}
        </div>
      )}
      {props.variant === "modern" && props.endIcon && (
        <div className="ml-auto flex items-center text-[18px] flex-shrink-0 text-creator-text-sub">
          {props.endIcon}
        </div>
      )}
      {props.label && label}
    </button>
  );
}
